// Cleans text that was copied from PDFs, Word, web pages or WhatsApp before it is
// saved. Such text often carries characters you cannot see — zero-width spaces, a
// byte-order mark, soft hyphens, right-to-left marks, non-breaking spaces, stray
// control codes, half of a broken emoji. They make a GSTIN look right but fail
// checks, eat into a field's maxlength, and can break the JSON sent to the server.
//
// The server applies the same rules (Helper/TextCleaner.cs), so keep the two in step.

// Characters that are never wanted in a typed value: zero-width space/joiners, word
// joiner, BOM, soft hyphen, Mongolian vowel separator, and the bidi marks/overrides.
const INVISIBLE = /[\u200B-\u200D\u2060\uFEFF\u00AD\u180E\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
// C0/C1 control codes except tab, line feed and carriage return.
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g;
// Half of a surrogate pair with no partner (a broken emoji).
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

export interface CleanResult {
  text: string;
  /** True when something other than plain spacing was removed or replaced. */
  removedHidden: boolean;
}

/**
 * Cleans one value. Single-line values have line breaks and tabs turned into a
 * space; multi-line values keep their line breaks. Runs of spaces become one space.
 * Leading/trailing spaces are NOT trimmed here (a pasted chunk may sit between words);
 * trim the whole field value when you read it.
 */
export function cleanText(raw: string | null | undefined, multiline = false): CleanResult {
  if (!raw) return { text: '', removedHidden: false };

  // Tab / CR / LF are excluded from CONTROL, so they don't count as "hidden" here.
  const removedHidden = hasMatch(raw, INVISIBLE) || hasMatch(raw, CONTROL) || hasMatch(raw, LONE_SURROGATE);

  let s = raw.replace(LONE_SURROGATE, '');
  // NFKC folds look-alike forms into plain ones: the "fi" ligature (U+FB01) → "fi",
  // full-width letters and digits → "ABC123", non-breaking and thin spaces → a normal space.
  s = s.normalize('NFKC');
  s = s.replace(INVISIBLE, '').replace(CONTROL, '');
  // Curly quotes, typographic dashes, ellipsis and bullets → plain keyboard forms
  // (GST e-invoice / e-way bill files and many printers reject the fancy ones).
  s = s
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/\u2022/g, '-');

  if (multiline) {
    s = s.replace(/\r\n?|[\u2028\u2029\u0085]/g, '\n').replace(/[^\S\n]+/g, ' ');
  } else {
    s = s.replace(/\s+/g, ' ');
  }

  return { text: s, removedHidden };
}

/**
 * Cleans a code such as a GSTIN, PAN, TAN or PIN code: everything `cleanText` does,
 * plus every space, hyphen, dot and slash is removed and letters are upper-cased.
 * "29 ABCDE-1234 F1Z5" → "29ABCDE1234F1Z5".
 */
export function cleanCode(raw: string | null | undefined): CleanResult {
  const r = cleanText(raw);
  return { text: r.text.replace(/[\s\-./]/g, '').toUpperCase(), removedHidden: r.removedHidden };
}

function hasMatch(s: string, re: RegExp): boolean {
  re.lastIndex = 0;
  const found = re.test(s);
  re.lastIndex = 0;
  return found;
}
